import React from 'react';
import { DonutLegendChart } from '@/components/ui/DonutLegendChart';
import type { ProjectSummary, ProjectDashboardItem } from '../types/projects1.types';
import { formatBillionCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

interface ProjectChartsProps {
  summary: ProjectSummary;
  upcoming: ProjectDashboardItem[];
  isLoading: boolean;
}

export function ProjectCharts({ summary, upcoming, isLoading }: ProjectChartsProps) {
  // Chart 1: Tỷ lệ tiến độ dự án
  const progressData = [
    { name: 'Hoàn thành', value: summary.completed, color: '#065f46' }, // emerald-800
    { name: 'Đang thực hiện', value: summary.active, color: '#2563eb' }, // blue-600
    { name: 'Chuẩn bị', value: summary.planning, color: '#f59e0b' }, // amber-500
    { name: 'Tạm dừng', value: summary.paused, color: '#f97316' }, // orange-500
  ];

  // Chart 2: Giá trị hợp đồng theo loại dự án
  const typeData = [
    { name: 'Thi công', value: summary.totalContractConstruction, color: '#2563eb' },
    { name: 'Bảo dưỡng', value: summary.totalContractMaintenance, color: '#059669' },
  ];

  return (
    <div className="flex flex-col space-y-4">
      {/* 1. Tỷ lệ tiến độ dự án */}
      <div className="rounded-md border border-slate-100 bg-white p-3  space-y-2">
        <h3 className="text-xs lg:text-sm font-normal text-slate-900 tracking-wider border-b border-slate-50 pb-2">Tỷ lệ tiến độ dự án</h3>
        <div className="pt-2">
          <DonutLegendChart
            title=""
            centerValue={summary.total}
            centerLabel="Tổng dự án"
            data={progressData}
            total={summary.total}
            className="border-none  p-0"
          />
        </div>
      </div>

      {/* 2. Giá trị hợp đồng theo loại dự án */}
      <div className="rounded-md border border-slate-100 bg-white p-3  space-y-2">
        <h3 className="text-xs lg:text-sm font-normal text-slate-900 tracking-wider border-b border-slate-50 pb-2">Giá trị hợp đồng theo loại dự án</h3>
        <div className="pt-2">
          <DonutLegendChart
            title=""
            centerValue={formatBillionCurrency(summary.totalContract)}
            centerLabel="Tỷ VND"
            data={typeData}
            total={summary.totalContract}
            valueFormatter={(item) => `${formatBillionCurrency(item.value)} tỷ`}
            className="border-none  p-0"
          />
        </div>
      </div>

      {/* 3. Dự án sắp tới hạn */}
      <div className="rounded-md border border-slate-100 bg-white p-3  space-y-2">
        <div className="flex items-center justify-between border-b border-slate-50 pb-2">
          <h3 className="text-xs lg:text-sm font-normal text-slate-900 tracking-wider">Dự án sắp tới hạn</h3>
          <button className="text-xs lg:text-sm font-normal text-emerald-600 hover:underline">Xem tất cả</button>
        </div>

        <div className="space-y-4 pt-1 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-200">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="h-10 w-14 rounded bg-slate-50" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-3/4 rounded bg-slate-50" />
                  <div className="h-2 w-1/2 rounded bg-slate-50" />
                </div>
              </div>
            ))
          ) : upcoming.length > 0 ? (
            upcoming.map((proj) => {
              const daysLeft = proj.end_date ? Math.ceil((new Date(proj.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;
              return (
                <div key={proj.id} className="flex gap-3 group cursor-pointer p-1">
                  <div className="h-10 w-14 shrink-0 overflow-hidden rounded bg-slate-100 border border-slate-50">
                    {proj.thumbnail_url ? (
                      <img src={proj.thumbnail_url} alt="" className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-200">
                        <i className="ri-image-line text-lg" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs lg:text-sm font-normal text-slate-900 truncate leading-tight group-hover:text-emerald-600 transition-colors">{proj.name}</p>
                    <p className="text-xs lg:text-sm font-normal text-slate-400 mt-0.5">{proj.code}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs lg:text-sm font-normal text-slate-600">{proj.end_date ? new Date(proj.end_date).toLocaleDateString('vi-VN') : '--/--/----'}</p>
                    <p className={cn(
                      "text-xs lg:text-sm font-normal mt-0.5 whitespace-nowrap",
                      daysLeft <= 7 ? "text-rose-500" : daysLeft <= 15 ? "text-orange-500" : "text-emerald-500"
                    )}>
                      {daysLeft > 0 ? `Còn ${daysLeft} ngày` : daysLeft === 0 ? 'Hết hạn hôm nay' : 'Quá hạn'}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs lg:text-sm text-slate-400 italic text-center py-4">Không có dự án nào sắp tới hạn.</p>
          )}
        </div>
      </div>
    </div>
  );
}
